import React, { useCallback, useEffect, useState } from 'react'
import {
    Box,
    Button,
    HStack,
    Icon,
    IconButton,
    Popover,
    PopoverAnchor,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    Portal,
    Text,
    Tooltip,
} from '@chakra-ui/react'
import { FaArrowRight, FaXmark } from 'react-icons/fa6'

import { useUserContext } from '~config/user-context'

import { useAskChckvetDock } from './ask-chckvet-context'
import { useAskChckvetIntroPrompt } from './use-ask-chckvet-intro-prompt'
import {
    askChckvetMascotLabel,
    type ClinicMascotIdentity,
    MascotAvatar,
    MASCOT_PEEK_PAW_BOTTOM_RATIO,
    MASCOT_TILT_CLASS,
    useClinicMascot,
} from './mascot'

/**
 * The pill is short and sits low in the header row on purpose: the mascot
 * peeks over its top edge, and the header only offers ~16px above a normally
 * sized button. Shrinking the pill and dropping it into the card's bottom
 * padding buys the head enough room without making the header taller (which
 * would cost every page vertical space via HEADER_HEIGHT).
 */
const BUTTON_HEIGHT = 26
const BUTTON_DROP = 7
const MASCOT_SIZE = 32
/** How far the toes sit below the button's top edge. */
const PAW_OVERLAP = 10

/**
 * Offset for the SVG's bottom edge. The peek box extends below the paws, so
 * that gap has to be added back or the paws end up merely touching the pill.
 */
const MASCOT_BOTTOM_OFFSET = Math.round(
    PAW_OVERLAP + MASCOT_SIZE * (1 - MASCOT_PEEK_PAW_BOTTOM_RATIO),
)

/**
 * Header entry point for the Ask Chckvet dock. Renders nothing when Ask
 * Chckvet isn't available for this clinic + caller.
 *
 * Hosts two nudges, never at once: the "Have a question?" intro popover,
 * then a "Customize your mascot!" pill. Dismissing either one writes a
 * per-user localStorage flag so that nudge does not open again.
 */
export default function AskChckvetTrigger() {
    const { isEnabled, isOpen, toggle, open } = useAskChckvetDock()
    const { userId } = useUserContext()
    const { mascot, clinicId } = useClinicMascot()
    const [isNudgeDismissed, setIsNudgeDismissed] = useState(true)
    const { isVisible: isIntroVisible, dismiss: dismissIntro } =
        useAskChckvetIntroPrompt({ isEnabled, isDockOpen: isOpen })

    useEffect(() => {
        setIsNudgeDismissed(readMascotNudgeDismissed(userId, clinicId))
    }, [userId, clinicId])

    const dismissNudge = useCallback(() => {
        writeMascotNudgeDismissed(userId)
        setIsNudgeDismissed(true)
    }, [userId])

    const handleNudgeClick = useCallback(() => {
        if (!isOpen) open()
    }, [isOpen, open])

    const handleTriggerClick = useCallback(() => {
        dismissIntro()
        toggle()
    }, [dismissIntro, toggle])

    const handleIntroAsk = useCallback(() => {
        dismissIntro()
        open()
    }, [dismissIntro, open])

    if (!isEnabled) return null

    const label = askChckvetMascotLabel(mascot)
    const tooltip = isOpen
        ? `Hide ${askChckvetMascotLabel(mascot)}`
        : askChckvetMascotLabel(mascot)

    return (
        <HStack spacing={2} align="center">
            {!isNudgeDismissed && !isIntroVisible && (
                <HStack
                    spacing={1}
                    bg="brand.500"
                    rounded="full"
                    pl={3}
                    pr={1}
                    py="2px"
                    shadow="md"
                    flexShrink={0}
                >
                    <Button
                        variant="link"
                        color="white"
                        size="xs"
                        fontWeight="semibold"
                        whiteSpace="nowrap"
                        rightIcon={<Icon as={FaArrowRight} boxSize={3} />}
                        onClick={handleNudgeClick}
                    >
                        Customize your mascot!
                    </Button>
                    <IconButton
                        aria-label="Dismiss customize mascot suggestion"
                        icon={<Icon as={FaXmark} boxSize={3} />}
                        size="xs"
                        variant="ghost"
                        color="white"
                        _hover={{ bg: 'whiteAlpha.300' }}
                        onClick={dismissNudge}
                    />
                </HStack>
            )}
            <Popover
                isOpen={isIntroVisible}
                onClose={dismissIntro}
                placement="bottom-end"
                closeOnBlur={false}
                autoFocus={false}
                returnFocusOnClose={false}
                isLazy
            >
                <PopoverAnchor>
                    <Box
                        display="flex"
                        alignSelf="flex-end"
                        mb={`-${BUTTON_DROP}px`}
                    >
                        <AskChckvetPillButton
                            mascot={mascot}
                            label={label}
                            tooltip={tooltip}
                            isOpen={isOpen}
                            onClick={handleTriggerClick}
                        />
                    </Box>
                </PopoverAnchor>
                <Portal>
                    <AskChckvetIntroContent
                        mascot={mascot}
                        onAsk={handleIntroAsk}
                    />
                </Portal>
            </Popover>
        </HStack>
    )
}

/**
 * "Have a question?" card. Closing it (X, Esc, or opening Ask Rocket)
 * stores a dismissal so it does not open again. Asking a question retires
 * it the same way.
 */
function AskChckvetIntroContent({
    mascot,
    onAsk,
}: {
    mascot: ClinicMascotIdentity
    onAsk: () => void
}) {
    return (
        <PopoverContent w="272px" shadow="lg">
            <PopoverArrow />
            <PopoverCloseButton aria-label="Dismiss Ask Chckvet suggestion" />
            <PopoverBody p={4}>
                <HStack spacing={3} align="center" pr={5}>
                    <Box flexShrink={0}>
                        <MascotAvatar mascot={mascot} size={44} />
                    </Box>
                    <Box>
                        <Text fontWeight="semibold" color="navy.700">
                            Have a question?
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                            Ask your personal Chckvet AI!
                        </Text>
                    </Box>
                </HStack>
                <Button
                    mt={3}
                    size="sm"
                    w="full"
                    colorScheme="brand"
                    rightIcon={<Icon as={FaArrowRight} boxSize={3} />}
                    onClick={onAsk}
                >
                    Ask a question
                </Button>
            </PopoverBody>
        </PopoverContent>
    )
}

function AskChckvetPillButton({
    mascot,
    label,
    tooltip,
    isOpen,
    onClick,
}: {
    mascot: ClinicMascotIdentity
    label: string
    tooltip: string
    isOpen: boolean
    onClick: () => void
}) {
    return (
        <Tooltip label={tooltip} openDelay={400}>
            <Button
                size="sm"
                h={`${BUTTON_HEIGHT}px`}
                variant={isOpen ? 'solid' : 'outline'}
                colorScheme="brand"
                rounded="full"
                position="relative"
                overflow="visible"
                onClick={onClick}
                aria-pressed={isOpen}
                sx={{
                    // Only the head tilts — the paws stay gripped on the pill.
                    [`& .${MASCOT_TILT_CLASS}`]: {
                        transition: 'transform 180ms ease',
                    },
                    [`&:hover .${MASCOT_TILT_CLASS}`]: {
                        transform: 'rotate(-7deg)',
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        [`& .${MASCOT_TILT_CLASS}`]: { transition: 'none' },
                        [`&:hover .${MASCOT_TILT_CLASS}`]: {
                            transform: 'none',
                        },
                    },
                }}
            >
                <Box
                    position="absolute"
                    left="50%"
                    transform="translateX(-50%)"
                    bottom={`calc(100% - ${MASCOT_BOTTOM_OFFSET}px)`}
                    pointerEvents="none"
                    overflow="visible"
                >
                    <MascotAvatar
                        mascot={mascot}
                        pose="peek"
                        size={MASCOT_SIZE}
                    />
                </Box>
                {label}
            </Button>
        </Tooltip>
    )
}

const MASCOT_NUDGE_STORAGE_PREFIX =
    'chckup.askChckvet.mascot-nudge.dismissed.v1'

function mascotNudgeKey(userId: string): string {
    return `${MASCOT_NUDGE_STORAGE_PREFIX}:user:${userId}`
}

/**
 * Earlier builds stored dismissal per clinic. Keep honoring that key so a
 * clinic that already dismissed the nudge does not see it again.
 */
function legacyClinicMascotNudgeKey(clinicId: string): string {
    return `${MASCOT_NUDGE_STORAGE_PREFIX}:${clinicId}`
}

/**
 * True when this user has dismissed the nudge. Hidden until we know the
 * user, and hidden when storage cannot be read, so it never nags in a loop.
 * A dismissal stored per clinic by an older build still counts.
 */
function readMascotNudgeDismissed(
    userId: string | undefined,
    clinicId: string | undefined,
): boolean {
    if (typeof window === 'undefined' || !userId) return true
    try {
        if (window.localStorage.getItem(mascotNudgeKey(userId)) === 'true') {
            return true
        }
        return Boolean(
            clinicId &&
            window.localStorage.getItem(
                legacyClinicMascotNudgeKey(clinicId),
            ) === 'true',
        )
    } catch {
        return true
    }
}

function writeMascotNudgeDismissed(userId: string | undefined): void {
    if (!userId || typeof window === 'undefined') return
    try {
        window.localStorage.setItem(mascotNudgeKey(userId), 'true')
    } catch {
        // Private mode / quota — nudge just shows again next visit.
    }
}
